import React, { useMemo } from 'react';
import { Typography, Paper, Alert } from '@mui/material';
import DOMPurify from 'dompurify';
import AudioPlayer from '../AudioPlayer';
import { buildAudioUrl } from '../../utils/urlUtils';

interface Question {
  id: number;
  text: string;
  options: string[];
  correctAnswer: number;
  userAnswer?: number;
  isMarkedForReview?: boolean;
  mediaUrl?: string; // Add mediaUrl support
}

interface QuestionDisplayProps {
  question: Question;
  questionNumber: number;
  totalQuestions: number;
}

// Utility function to validate audio URLs
const isValidAudioUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;

  const supportedFormats = ['.mp3', '.wav', '.ogg', '.m4a', '.aac'];

  // Handle both relative and absolute URLs
  let pathname: string;
  try {
    if (url.startsWith('http')) {
      // Full URL
      const urlObj = new URL(url);
      pathname = urlObj.pathname.toLowerCase();
    } else {
      // Relative URL - just use the path directly
      pathname = url.toLowerCase();
    }

    // Check if URL ends with supported format or has audio path
    const hasAudioFormat = supportedFormats.some(format => pathname.endsWith(format));
    const hasAudioPath = pathname.includes('/audio/');

    return hasAudioFormat || hasAudioPath;
  } catch {
    return false;
  }
};

// Utility function to sanitize HTML content
const sanitizeHtml = (html: string): string => {
  if (!html || typeof html !== 'string') return '';

  // Configure DOMPurify to allow safe HTML elements for educational content including images
  const cleanHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'sub', 'sup', 'span', 'div', 'img'],
    ALLOWED_ATTR: ['class', 'style', 'src', 'alt', 'width', 'height']
  });

  return cleanHtml;
};

const QuestionDisplay: React.FC<QuestionDisplayProps> = ({
  question,
  questionNumber,
  totalQuestions
}) => {
  // Memoize sanitized HTML to prevent unnecessary re-sanitization
  const sanitizedQuestionText = useMemo(() => {
    return sanitizeHtml(question.text);
  }, [question.text]);

  // Build full audio URL from relative path and validate
  const audioUrl = useMemo(() => {
    return question.mediaUrl ? buildAudioUrl(question.mediaUrl) : null;
  }, [question.mediaUrl]);

  const isAudioUrlValid = useMemo(() => {
    return audioUrl ? isValidAudioUrl(audioUrl) : false;
  }, [audioUrl]);

  return (
    <>
      {/* Audio Player for Listening Questions */}
      {audioUrl && (
        <>
          {isAudioUrlValid ? (
            <AudioPlayer
              audioUrl={audioUrl}
              title={`Question ${questionNumber} Audio`}
              autoPlay={false}
            />
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Audio file is not available or in an unsupported format.
            </Alert>
          )}
        </>
      )}

      {/* Question Text */}
      <Paper sx={{ p: 3, mb: 2 }}>
        {sanitizedQuestionText ? (
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              lineHeight: 1.6,
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                margin: '10px auto',
                borderRadius: 1,
                boxShadow: 1
              }
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedQuestionText }}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{ mb: 3, lineHeight: 1.6, fontStyle: 'italic', color: 'text.secondary' }}
          >
            Question content is not available.
          </Typography>
        )}
      </Paper>
    </>
  );
};

export default QuestionDisplay;